import {forwardRef, useContext, useEffect, useImperativeHandle, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {Alert, Flex, Form, Modal, Radio, Result, Space, Spin, Tag, Typography,} from 'antd'
import {ExclamationCircleOutlined, InfoCircleOutlined,} from '@ant-design/icons'
import {NavContext} from '../../context/global_context'
import {getRequest, postRequest} from '../../services/base'

const { Text } = Typography

const SelectVersionModal = forwardRef(({
  vehicle,
  vehicleName = '',
  branch = '',
  onBack = () => {},
  onStatusChange = () => {},
}, ref) => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const [, navDispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()
  const [form] = Form.useForm()

  const [versionsData, setVersionsData] = useState({})
  const [dataError, setDataError] = useState(null)
  const [loading, setLoading] = useState(false)

  const dataVersion = searchParams.get('data_version') || ''

  useEffect(() => {
    fetchVersions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle])

  const fetchVersions = async () => {
    setDataError(null)
    setLoading(true)
    setVersionsData({})
    form.resetFields()

    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'tracerPreview.loadingVersions' }),
        showLoader: true,
      },
      notification: {},
    })

    try {
      let res = await getRequest(
        `/api/dtc_validation/tracer-data-version/${vehicle}/?data_version=${dataVersion}`
      )
      if (
        !res?.status ||
        (!res?.data?.data_version && !res?.data?.app_version)
      ) {
        setDataError(res?.data || {})
      } else {
        setVersionsData(res.data || {})
      }
    } catch {
      setDataError({
        message: intl.formatMessage({ id: 'tracerPreview.versionFetchError' }),
      })
    } finally {
      setLoading(false)
      navDispatch({ type: 'loader', data: {}, notification: {} })
    }
  }

  const initiatePreview = async () => {
    let version
    try {
      const values = await form.validateFields()
      version = values.data_version
    } catch {
      // Validation failed — antd already shows inline error
      return
    }
    if (!version || !versionsData.app_version) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'tracerPreview.pleaseSelectVersion' }),
        },
      })
      return
    }

    modal.confirm({
      title: intl.formatMessage({ id: 'tracerPreview.runPreviewTitle' }),
      icon: (
        <ExclamationCircleOutlined
          className="select-version-modal-icon"
        />
      ),
      content: (
        <Space wrap size={8}>
          <Text>
            {intl.formatMessage({ id: 'tracerPreview.appVersion' })}:
          </Text>
          <Tag color="blue">{versionsData.app_version}</Tag>
          <Text>
            {intl.formatMessage({ id: 'tracerPreview.dataVersion' })}:
          </Text>
          <Tag color="purple">{version}</Tag>
        </Space>
      ),
      centered: true,
      maskClosable: false,
      okText: intl.formatMessage({ id: 'forms.confirm' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        navDispatch({
          type: 'loader',
          data: {
            caption: intl.formatMessage({
              id: 'tracerPreview.initiatingPreview',
            }),
            showLoader: true,
          },
          notification: {},
        })

        try {
          let res = await postRequest(
            `/api/tracer/preview/start/?vehicle=${vehicle}`,
            {
              tracer_version: versionsData.app_version,
              data_version: version,
            }
          )
          if (res?.status) {
            navDispatch({
              type: 'notification',
              data: {
                notification_type: 'success',
                msg: intl.formatMessage({ id: 'tracerPreview.previewInitiated' }),
              },
            })
            setTimeout(() => window.location.reload(), 1000)
          } else {
            // start answers 400 only when another portal's preview is active.
            navDispatch({
              type: 'notification',
              data: {
                notification_type: 'error',
                msg: intl.formatMessage({
                  id:
                    res?.httpStatus === 400
                      ? 'tracerPreview.previewInUseDesc'
                      : 'tracerPreview.somethingWentWrong',
                }),
              },
            })
          }
        } catch {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg: intl.formatMessage({
                id: 'tracerPreview.somethingWentWrong',
              }),
            },
          })
        }
      },
    })
  }

  useImperativeHandle(ref, () => ({
    initiatePreview,
    retry: fetchVersions,
  }))

  const isFromMaster = searchParams.get('data_version') === 'master'
  const showVersionOption =
    versionsData.data_version !== '' && versionsData.data_version !== branch
  const showBranchOption = !isFromMaster && branch !== ''

  useEffect(() => {
    onStatusChange({
      hasError: !!dataError,
      hasVersions: showVersionOption || showBranchOption,
    })
  }, [dataError, showVersionOption, showBranchOption])

  return (
    <Flex vertical gap={16} align="center" className="select-version-modal">
      {contextHolder}

      {vehicleName && (
        <Text strong>
          {vehicleName}
        </Text>
      )}

      {loading ? (
        <Flex vertical gap={12} align="center" className="select-version-modal-loading">
          <Spin size="large" />
          <Text type="secondary">
            {intl.formatMessage({ id: 'tracerPreview.fetchingVersionData' })}
          </Text>
        </Flex>
      ) : dataError ? (
        <Flex vertical gap={16}>
          <Result
            status="warning"
            title={intl.formatMessage({ id: 'tracerPreview.versionNotAvailable' })}
            subTitle={
              dataError?.message ||
              dataError?.detail ||
              intl.formatMessage({ id: 'tracerPreview.versionFetchError' })
            }
          />
        </Flex>
      ) : (
        <Form
          form={form}
          layout="vertical"
          className="select-version-modal-form"
          initialValues={{ data_version: null }}
        >
          {versionsData.app_version && (
            <Alert
              message={intl.formatMessage(
                {
                  id: 'tracerPreview.appVersionAvailable',
                },
                { version: versionsData.app_version }
              )}
              description={intl.formatMessage({
                id: 'tracerPreview.appVersionDesc',
              })}
              type="info"
              showIcon
            />
          )}

          {(showVersionOption || showBranchOption) && (
            <Form.Item
              name="data_version"
              label={
                <Space>
                  <Text strong>
                    {intl.formatMessage({ id: 'tracerPreview.dataVersion' })}
                  </Text>
                  <InfoCircleOutlined
                    className="select-version-info-icon"
                  />
                </Space>
              }
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'tracerPreview.pleaseSelectVersion',
                  }),
                },
              ]}
            >
              <div className="select-version-modal-radio-group">
                <Radio.Group className="select-version-modal-form">
                  {showVersionOption && (
                    <Radio
                      value={`v${versionsData.data_version}`}
                      className="select-version-modal-radio-option"
                    >
                      <Flex align="center" gap={8} className="select-version-modal-radio-content">
                        <Flex vertical className="select-version-modal-radio-label">
                          <Text strong>v{versionsData.data_version}</Text>
                        </Flex>
                        <Tag color="purple">
                          {intl.formatMessage(
                            { id: 'tracerPreview.dataVersionOption' },
                            { version: versionsData.data_version }
                          )}
                        </Tag>
                      </Flex>
                    </Radio>
                  )}
                  {showBranchOption && (
                    <Radio
                      value={branch}
                      className="select-version-modal-radio-option"
                    >
                      <Flex align="center" gap={8} className="select-version-modal-radio-content">
                        <Flex vertical className="select-version-modal-radio-label">
                          <Text strong>{branch}</Text>
                        </Flex>
                        <Tag color="blue">
                          {intl.formatMessage(
                            { id: 'tracerPreview.branchOption' },
                            { branch }
                          )}
                        </Tag>
                      </Flex>
                    </Radio>
                  )}
                </Radio.Group>
              </div>
            </Form.Item>
          )}

          {!showVersionOption && !showBranchOption && !dataError && (
            <Result
              status="info"
              title={intl.formatMessage({
                id: 'tracerPreview.noVersionsAvailable',
              })}
              subTitle={intl.formatMessage({
                id: 'tracerPreview.noVersionsDesc',
              })}
            />
          )}
        </Form>
      )}
    </Flex>
  )
})

export default SelectVersionModal
